import { useCallback, useEffect } from 'react';
import { EditMessagePayload, GetMessagesParams, SendMessagePayload } from 'services/chat';
import { useChatStore } from '../store/chatStore';
import { useMessageStore } from '../store/messageStore';

/**
 * Hook for fetching and managing messages for a specific channel
 * Replaces useMessages from SWR
 */
export const useZustandMessages = (channelId: string | null, params: GetMessagesParams = {}) => {
	const {
		getChannelMessages,
		getPendingMessages,
		fetchMessages,
		loadMoreMessages,
		loadingStates,
		paginationState,
		clearChannelData,
	} = useMessageStore();

	const messages = channelId ? getChannelMessages(channelId) : [];
	const pendingMessages = channelId ? getPendingMessages(channelId) : [];
	const loadingState = channelId ? loadingStates[channelId] : null;
	const pagination = channelId ? paginationState[channelId] : null;

	// Combine API messages with pending messages
	const combinedMessages = [...messages, ...pendingMessages];

	// Fetch messages when channel changes
	useEffect(() => {
		if (channelId && channelId.trim() !== '') {
			// Clear previous channel data and fetch new messages
			clearChannelData(channelId);
			fetchMessages(channelId, { limit: 20, order: 'desc', ...params });
		}
	}, [channelId, fetchMessages, clearChannelData]);

	const handleLoadMore = useCallback(async () => {
		if (channelId && !loadingState?.isLoadingMore && !loadingState?.isReachingEnd) {
			try {
				await loadMoreMessages(channelId);
			} catch (error) {
				console.error('Error loading more messages:', error);
			}
		}
	}, [channelId, loadMoreMessages, loadingState]);

	return {
		messages: combinedMessages,
		isLoading: loadingState?.isLoading || false,
		isLoadingMore: loadingState?.isLoadingMore || false,
		isReachingEnd: loadingState?.isReachingEnd || false,
		error: loadingState?.error || null,
		loadMore: handleLoadMore,
		pagination,
	};
};

/**
 * Hook for sending messages
 * Replaces useSendMessage from SWR
 */
export const useZustandSendMessage = () => {
	const { sendMessage, addPendingMessage, updatePendingMessage, operationStates } =
		useMessageStore();

	const handleSendMessage = useCallback(
		async (channelId: string, payload: SendMessagePayload) => {
			try {
				const result = await sendMessage(channelId, payload);
				return { success: true, data: result, error: null };
			} catch (error) {
				console.error('Error sending message:', error);
				return { success: false, data: null, error };
			}
		},
		[sendMessage]
	);

	return {
		sendMessage: handleSendMessage,
		isSending: (messageId: string) => operationStates.sending[messageId] || false,
		addPendingMessage,
		updatePendingMessage,
	};
};

/**
 * Hook for editing messages
 * Replaces useEditMessage from SWR
 */
export const useZustandEditMessage = () => {
	const { editMessage, operationStates } = useMessageStore();

	const handleEditMessage = useCallback(
		async (channelId: string, messageId: string, payload: EditMessagePayload) => {
			try {
				const result = await editMessage(channelId, messageId, payload);
				return { data: result, error: null };
			} catch (error) {
				console.error('Error editing message:', error);
				return { data: null, error };
			}
		},
		[editMessage]
	);

	return {
		editMessage: handleEditMessage,
		isEditing: (messageId: string) => operationStates.editing[messageId] || false,
	};
};

/**
 * Hook for deleting messages
 * Replaces useDeleteMessage from SWR
 */
export const useZustandDeleteMessage = () => {
	const { deleteMessageFromAPI, operationStates } = useMessageStore();

	const handleDeleteMessage = useCallback(
		async (channelId: string, messageId: string) => {
			try {
				const success = await deleteMessageFromAPI(channelId, messageId);
				return { error: success ? null : 'Failed to delete message' };
			} catch (error) {
				console.error('Error deleting message:', error);
				return { error };
			}
		},
		[deleteMessageFromAPI]
	);

	return {
		deleteMessage: handleDeleteMessage,
		isDeleting: (messageId: string) => operationStates.deleting[messageId] || false,
	};
};

/**
 * Hook for managing channel selection and related state
 * Enhances useChatStore functionality
 */
export const useZustandChannelSelection = () => {
	const { selectedChannelId, setSelectedChannelId } = useChatStore();
	const { clearChannelData } = useMessageStore();

	const selectChannel = useCallback(
		(channelId: string | null) => {
			if (channelId !== selectedChannelId) {
				setSelectedChannelId(channelId);
			}
		},
		[selectedChannelId, setSelectedChannelId]
	);

	const clearChannelMessages = useCallback(
		(channelId: string) => {
			clearChannelData(channelId);
		},
		[clearChannelData]
	);

	return {
		selectedChannelId,
		selectChannel,
		clearChannelMessages,
	};
};

/**
 * Hook for WebSocket integration with Zustand
 * Replaces WebSocket handlers in ChatPageWrapper
 */
export const useZustandWebSocket = () => {
	const {
		handleWebSocketMessage,
		handleWebSocketMessageEdit,
		handleWebSocketMessageDelete,
		removePendingMessage,
	} = useMessageStore();

	const handleNewMessage = useCallback(
		(message: any) => {
			// Convert WebSocket message to API message format if needed
			handleWebSocketMessage(message);
		},
		[handleWebSocketMessage]
	);

	const handleEditedMessage = useCallback(
		(messageId: string, channelId: string, updatedMessage: any) => {
			handleWebSocketMessageEdit(messageId, channelId, updatedMessage);
		},
		[handleWebSocketMessageEdit]
	);

	const handleDeletedMessage = useCallback(
		(messageId: string, channelId: string) => {
			handleWebSocketMessageDelete(messageId, channelId);
		},
		[handleWebSocketMessageDelete]
	);

	const handleMessageConfirmation = useCallback(
		(messageId: string, channelId: string) => {
			// Remove from pending when confirmed by WebSocket
			removePendingMessage(channelId, messageId);
		},
		[removePendingMessage]
	);

	return {
		handleNewMessage,
		handleEditedMessage,
		handleDeletedMessage,
		handleMessageConfirmation,
	};
};

/**
 * Hook for managing message drafts and editor state
 */
export const useZustandMessageEditor = () => {
	const { getEditorDraft, setEditorDraft, getChannelDraft, setChannelDraft } = useMessageStore();

	const getDraft = useCallback(
		(channelId: string) => {
			return getEditorDraft(channelId);
		},
		[getEditorDraft]
	);

	const saveDraft = useCallback(
		(channelId: string, content: string) => {
			setEditorDraft(channelId, content);
		},
		[setEditorDraft]
	);

	const getMessageDraft = useCallback(
		(channelId: string) => {
			return getChannelDraft(channelId);
		},
		[getChannelDraft]
	);

	const saveMessageDraft = useCallback(
		(channelId: string, draft: any) => {
			setChannelDraft(channelId, draft);
		},
		[setChannelDraft]
	);

	return {
		getDraft,
		saveDraft,
		getMessageDraft,
		saveMessageDraft,
	};
};

/**
 * Hook for message operations status
 * Provides loading states for UI feedback
 */
export const useZustandMessageOperations = () => {
	const { operationStates, loadingStates } = useMessageStore();

	const getOperationStatus = useCallback(
		(messageId: string) => {
			return {
				isSending: operationStates.sending[messageId] || false,
				isEditing: operationStates.editing[messageId] || false,
				isDeleting: operationStates.deleting[messageId] || false,
			};
		},
		[operationStates]
	);

	const getChannelStatus = useCallback(
		(channelId: string) => {
			return (
				loadingStates[channelId] || {
					isLoading: false,
					isLoadingMore: false,
					isReachingEnd: false,
					error: null,
				}
			);
		},
		[loadingStates]
	);

	return {
		getOperationStatus,
		getChannelStatus,
	};
};
